/** Must match posthog-js `_callDecideEndpoint` — Brave/ad-block lists often target `/decide` specifically. */
export const POSTHOG_DECIDE_PATH = '/decide/?v=4'
export const POSTHOG_REACHABILITY_TIMEOUT_MS = 8_000
export const POSTHOG_REACHABILITY_PROBE_DISTINCT_ID = 'posthog-reachability-probe'

export type PostHogBlockedReason = 'network_error' | 'timeout' | 'html_block_page'

export type PostHogReachabilityResult =
  | { status: 'reachable' }
  | { status: 'blocked'; reason: PostHogBlockedReason }

type FetchResponseLike = {
  status: number
  headers: { get: (name: string) => string | null }
}

type FetchInitLike = {
  method?: string
  headers?: Record<string, string>
  body?: string
  signal?: AbortSignal
}

export type FetchLike = (input: string, init?: FetchInitLike) => Promise<FetchResponseLike>

type CheckPostHogReachabilityOptions = {
  host: string
  apiKey: string
  fetchImpl?: FetchLike
  timeoutMs?: number
}

const isHtmlContentType = (contentType: string | null): boolean => {
  if (contentType == null) return false
  return contentType.toLowerCase().includes('text/html')
}

/**
 * Probes the same `/decide` endpoint posthog-js uses for feature flags.
 * Ad blockers (including Brave) commonly filter `/decide` while leaving
 * other PostHog paths alone — which is why a `/flags` probe can succeed
 * while procedures stay hidden (NOD-3773).
 */
export async function checkPostHogReachability({
  host,
  apiKey,
  fetchImpl = (input, init) => fetch(input, init),
  timeoutMs = POSTHOG_REACHABILITY_TIMEOUT_MS,
}: CheckPostHogReachabilityOptions): Promise<PostHogReachabilityResult> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetchImpl(`${host}${POSTHOG_DECIDE_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: apiKey,
        distinct_id: POSTHOG_REACHABILITY_PROBE_DISTINCT_ID,
      }),
      signal: controller.signal,
    })

    if (isHtmlContentType(response.headers.get('content-type'))) {
      return { status: 'blocked', reason: 'html_block_page' }
    }

    return { status: 'reachable' }
  } catch {
    if (controller.signal.aborted) {
      return { status: 'blocked', reason: 'timeout' }
    }
    return { status: 'blocked', reason: 'network_error' }
  } finally {
    clearTimeout(timeoutId)
  }
}
